/**
 * Property Watch — what just came on the market and is worth a look now.
 *
 * Deliberately an alert rather than a browse surface: a high Deal Score
 * threshold and a short window, so a glance is enough. Anything that lists
 * everything stops being worth glancing at, which is the whole point of the
 * panel sitting directly under the dashboard counters.
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowsUpDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from "@heroicons/react/24/outline";

import {
  usePropertyWatch,
  type PropertyWatchKind,
  type PropertyWatchSort,
} from "@/api/dashboard";
import { BrandedLoader } from "@/components/shared/BrandedLoader";

/** Kept at 10 — the panel is an alert, and a taller page would push the rest
 *  of the dashboard below the fold. Paging is how you see past it now. */
const PAGE_SIZE = 10;
const PAGE_DAYS = 14;

const SORTS: { key: PropertyWatchSort; label: string }[] = [
  { key: "added", label: "Newest" },
  { key: "score", label: "Score" },
  { key: "gap", label: "AVM gap" },
  { key: "price", label: "Price" },
];

function aed(n: number | null): string {
  if (n == null) return "—";
  if (n >= 1_000_000) return `AED ${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1_000) return `AED ${Math.round(n / 1_000)}k`;
  return `AED ${Math.round(n).toLocaleString()}`;
}

/** "3d ago" reads faster than a date when the window is only two weeks. */
function ago(iso: string | null): string {
  if (!iso) return "—";
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000);
  if (days <= 0) return "today";
  if (days === 1) return "yesterday";
  return `${days}d ago`;
}

function scoreTone(score: number | null): string {
  if (score == null) return "bg-gray-100 text-gray-600";
  if (score >= 80) return "bg-emerald-100 text-emerald-800";
  if (score >= 75) return "bg-lime-100 text-lime-800";
  return "bg-amber-100 text-amber-800";
}

export function PropertyWatchPanel({
  kind,
  title,
}: {
  kind: PropertyWatchKind;
  title: string;
}) {
  const [sort, setSort] = useState<PropertyWatchSort>("added");
  const [offset, setOffset] = useState(0);
  const { data, isLoading, isError } = usePropertyWatch(
    kind,
    sort,
    PAGE_DAYS,
    PAGE_SIZE,
    offset,
  );

  const rows = data?.items ?? [];
  const total = data?.total ?? 0;
  const firstShown = total === 0 ? 0 : offset + 1;
  const lastShown = Math.min(offset + PAGE_SIZE, total);
  const canPrev = offset > 0;
  const canNext = offset + PAGE_SIZE < total;

  /** Changing the sort re-orders the whole set, so page 4 of the old order is
   *  meaningless in the new one. Reset to the top rather than leaving the
   *  operator somewhere arbitrary. */
  function changeSort(next: PropertyWatchSort) {
    setSort(next);
    setOffset(0);
  }

  const detailPath = (id: string) =>
    kind === "sale" ? `/properties/${id}` : `/rental-properties/${id}`;

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-3">
      <div className="mb-2 flex items-center justify-between gap-2">
        <div>
          <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
          <p className="text-xs text-gray-500">
            Deal Score 70+ · listed in the last 14 days
            {/* The narrowing is on the sale branch only: rental stock has
                no completion status and a tenancy has no primary market
                (MPPI-00857). Saying it on the rent panel would be a claim
                about a filter that is not there. */}
            {kind === "sale" ? " · secondary & ready" : ""}
          </p>
        </div>
        <div className="flex items-center gap-1">
          <ArrowsUpDownIcon className="h-3.5 w-3.5 text-gray-400" aria-hidden />
          {SORTS.map((s) => (
            <button
              key={s.key}
              type="button"
              onClick={() => changeSort(s.key)}
              aria-pressed={sort === s.key}
              className={`rounded px-1.5 py-0.5 text-[11px] ${
                sort === s.key
                  ? "bg-gray-900 text-white"
                  : "text-gray-500 hover:bg-gray-100"
              }`}
            >
              {s.label}
            </button>
          ))}
        </div>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-6">
          <BrandedLoader size={32} label={null} />
        </div>
      ) : isError ? (
        <p className="py-4 text-center text-xs text-rose-600">
          Could not load Property Watch.
        </p>
      ) : !rows.length ? (
        /* Say which condition produced the empty state. "No results" would
           leave the operator unable to tell a quiet fortnight from a broken
           query — the exact ambiguity that hid the Distressed Signals bug.
           Paging adds a third case: an offset past the end is not the same as
           nothing matching, and claiming it is would be the same mistake. */
        offset > 0 ? (
          <div className="py-4 text-center text-xs text-gray-500">
            <p>No more results past this point.</p>
            <button
              type="button"
              onClick={() => setOffset(0)}
              className="mt-1 text-primary hover:underline"
            >
              Back to the first page
            </button>
          </div>
        ) : (
          <p className="py-4 text-center text-xs text-gray-500">
            Nothing scored 70+ in the last {PAGE_DAYS} days.
          </p>
        )
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[520px] text-xs">
            <thead>
              <tr className="border-b border-gray-200 text-left text-gray-500">
                <th className="py-1.5 pr-2 font-medium">Property</th>
                <th className="py-1.5 pr-2 text-right font-medium">
                  {kind === "sale" ? "Price" : "Rent / yr"}
                </th>
                <th className="py-1.5 pr-2 text-right font-medium">Score</th>
                <th className="py-1.5 pr-2 text-right font-medium">AVM gap</th>
                <th className="py-1.5 text-right font-medium">Added</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr
                  key={r.property_id}
                  className="border-b border-gray-100 last:border-0 hover:bg-gray-50"
                >
                  <td className="py-1.5 pr-2">
                    <Link
                      to={detailPath(r.property_id)}
                      className="font-medium text-gray-900 hover:text-primary hover:underline"
                    >
                      {r.community ?? "Unknown community"}
                    </Link>
                    <span className="ml-1 text-gray-500">
                      {r.bedrooms != null ? `${r.bedrooms} bed` : ""}
                      {r.emirate ? ` · ${r.emirate}` : ""}
                    </span>
                  </td>
                  <td className="py-1.5 pr-2 text-right tabular-nums text-gray-700">
                    {aed(r.price_aed)}
                  </td>
                  <td className="py-1.5 pr-2 text-right">
                    <span
                      className={`rounded px-1.5 py-0.5 font-semibold tabular-nums ${scoreTone(
                        r.deal_score,
                      )}`}
                    >
                      {r.deal_score?.toFixed(1) ?? "—"}
                    </span>
                  </td>
                  <td className="py-1.5 pr-2 text-right tabular-nums">
                    {r.avm_gap_pct == null ? (
                      <span className="text-gray-400" title="No AVM for this property">
                        —
                      </span>
                    ) : (
                      <span
                        className={
                          r.avm_gap_pct > 0 ? "text-emerald-700" : "text-rose-700"
                        }
                        title={
                          r.avm_gap_pct > 0
                            ? "Asking below the model's valuation"
                            : "Asking above the model's valuation"
                        }
                      >
                        {r.avm_gap_pct > 0 ? "+" : ""}
                        {r.avm_gap_pct.toFixed(1)}%
                      </span>
                    )}
                  </td>
                  <td className="py-1.5 text-right text-gray-500">
                    {ago(r.added_at)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pager. Shown whenever there is more than one page — hidden entirely
          when everything fits, so the common case gains no clutter. */}
      {total > PAGE_SIZE && (
        <div className="mt-2 flex items-center justify-between border-t border-gray-100 pt-2">
          <p className="text-[11px] tabular-nums text-gray-500">
            {firstShown}–{lastShown} of {total.toLocaleString()}
          </p>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
              disabled={!canPrev}
              aria-label="Previous page"
              className="rounded p-1 text-gray-500 enabled:hover:bg-gray-100 disabled:opacity-30"
            >
              <ChevronLeftIcon className="h-4 w-4" aria-hidden />
            </button>
            <button
              type="button"
              onClick={() => setOffset(offset + PAGE_SIZE)}
              disabled={!canNext}
              aria-label="Next page"
              className="rounded p-1 text-gray-500 enabled:hover:bg-gray-100 disabled:opacity-30"
            >
              <ChevronRightIcon className="h-4 w-4" aria-hidden />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
